import BaseStockProvider from '../BaseStockProvider';

/**
 * Pixabay stock media provider
 * Free API: https://pixabay.com/api/docs/
 * 
 * Pixabay API Guidelines:
 * 1. Attribute Pixabay and the content creator
 * 2. Don't sell unmodified content
 * 3. Display "Image by [Creator] from Pixabay" or similar
 * 4. Rate limit: 5,000 requests per hour (with API key)
 */
class PixabayProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Pixabay',
      apiKey: apiKey || process.env.REACT_APP_PIXABAY_API_KEY,
      baseUrl: 'https://pixabay.com/api',
      mediaTypes: ['image', 'video'],
      requiresAuth: true,
    });
    // Note: Pixabay discontinued their music/audio API
  }

  /**
   * Search for media on Pixabay
   */
  async search({ query, mediaType = 'image', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Pixabay API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const endpoint = mediaType === 'video' ? '/videos' : '';
      const url = `${this.baseUrl}${endpoint}/?key=${this.apiKey}&q=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}&safesearch=true`;
      
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Pixabay API error: ${response.status}`);
      }

      const data = await response.json();
      const items = data.hits || [];
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: (page * perPage) < data.totalHits,
        total: data.totalHits,
        page: page,
      };
    } catch (error) {
      console.error('Pixabay search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get popular media (Pixabay doesn't have a dedicated popular endpoint, so we use general search)
   */
  async getPopular({ mediaType = 'image', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Pixabay API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const endpoint = mediaType === 'video' ? '/videos' : '';
      const url = `${this.baseUrl}${endpoint}/?key=${this.apiKey}&page=${page}&per_page=${perPage}&order=popular&safesearch=true`;
      
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Pixabay API error: ${response.status}`);
      }

      const data = await response.json();
      const items = data.hits || [];
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: (page * perPage) < data.totalHits,
        total: data.totalHits,
        page: page,
      };
    } catch (error) {
      console.error('Pixabay getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Normalize Pixabay item to common format
   */
  normalizeItem(item, mediaType) {
    if (mediaType === 'video') {
      return this.normalizeVideo(item);
    }
    return this.normalizePhoto(item);
  }

  /**
   * Normalize Pixabay photo
   */
  normalizePhoto(item) {
    return {
      id: `pixabay_photo_${item.id}`,
      provider: 'pixabay',
      name: item.tags || 'Untitled',
      thumbnail: item.previewURL,
      url: item.webformatURL,
      downloadUrl: item.largeImageURL,
      type: 'image',
      category: 'Stock',
      description: item.tags,
      size: `${(item.imageSize / (1024 * 1024)).toFixed(1)} MB`,
      width: item.imageWidth,
      height: item.imageHeight,
      author: {
        name: item.user,
        username: item.user,
        profileUrl: `https://pixabay.com/users/${item.user}-${item.user_id}/`,
      },
      sourceUrl: item.pageURL,
      attribution: `Image by ${item.user} from Pixabay`,
      attributionUrl: item.pageURL,
      isUserUpload: false,
      views: item.views,
      likes: item.likes,
      downloads: item.downloads,
    };
  }

  /**
   * Normalize Pixabay video
   */
  normalizeVideo(item) {
    // Get the best quality video
    const videoSizes = item.videos || {};
    const video = videoSizes.large || videoSizes.medium || videoSizes.small || videoSizes.tiny;
    
    return {
      id: `pixabay_video_${item.id}`,
      provider: 'pixabay',
      name: item.tags || 'Video',
      thumbnail: item.picture_id ? `https://i.vimeocdn.com/video/${item.picture_id}_295x166.jpg` : null,
      url: video?.url || '',
      downloadUrl: video?.url || '',
      type: 'video',
      category: 'Stock',
      description: item.tags,
      size: video?.size ? `${(video.size / (1024 * 1024)).toFixed(1)} MB` : null,
      width: video?.width,
      height: video?.height,
      duration: item.duration,
      author: {
        name: item.user,
        username: item.user,
        profileUrl: `https://pixabay.com/users/${item.user}-${item.user_id}/`,
      },
      sourceUrl: item.pageURL,
      attribution: `Video by ${item.user} from Pixabay`,
      attributionUrl: item.pageURL,
      isUserUpload: false,
      views: item.views,
      likes: item.likes,
      downloads: item.downloads,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item, mediaType = 'image') {
    const type = mediaType === 'video' ? 'Video' : 'Image';
    return item.attribution || `${type} by ${item.author?.name || 'Unknown'} from Pixabay`;
  }
}

export default PixabayProvider;